/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Exporter } from "ra-core";
import {
  fetchRelatedRecords,
  useDataProvider,
  useListContext,
  useNotify,
  useResourceContext,
} from "ra-core";
import { useCallback, useMemo } from "react";

/**
 * This fill will be backported to 'ra-core' in the future.
 *
 * @deprecated Import from 'ra-core' instead.
 */
export function useBulkExport<
  ResourceInformationsType extends Partial<{ resource: string }>,
>(props: UseBulkExportProps<ResourceInformationsType>) {
  const { exporter: customExporter, meta } = props;

  const resource = useResourceContext(props);
  const { exporter: exporterFromContext, selectedIds } = useListContext();
  const exporter = customExporter || exporterFromContext;
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const bulkExport = useCallback(() => {
    if (exporter && resource) {
      // Almikia Epic E3b (TASK-071): flag the read so the data provider
      // routes it through the server-side export path (TASK-066) instead of
      // a plain bulk read -- see
      // ADR-ee2f59a9-TASK-066-server-side-export-enforcement.md.
      dataProvider
        .getMany(resource, {
          ids: selectedIds,
          meta: { ...meta, export: true },
        })
        .then(({ data }) =>
          exporter(
            data,
            fetchRelatedRecords(dataProvider),
            dataProvider,
            resource,
          ),
        )
        .catch((error) => {
          console.error(error);
          notify("ra.notification.http_error", {
            type: "error",
          });
        });
    }
  }, [dataProvider, exporter, notify, resource, selectedIds, meta]);

  return useMemo(() => {
    return {
      bulkExport,
    };
  }, [bulkExport]);
}

export type ResourceInformation = Partial<{ resource: string }>;

export type UseBulkExportProps<T extends ResourceInformation> = T & {
  exporter?: Exporter;

  meta?: any;
};
